import * as React from 'react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  AlertTriangle,
  Info,
  Cpu,
  Zap,
  Binary,
  TrendingUp,
  GitCompare,
  Table,
  Settings2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts'
import {
  parseRawTable,
  computeColumnStats,
  applyTransform,
  getPresets,
} from '@/lib/dataTransformer'
import type {
  RawTableData,
  TransformConfig,
  TransformRule,
  ColumnStats,
  TransformPreset,
  ColumnType,
} from '@/types'
import type { ParseResult } from '@/lib/fileParser'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DataTransformerProps {
  rawText: string
  fileName: string
  fileSize: number
  onComplete: (result: ParseResult) => void
  onCancel: () => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DELIMITER_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: ',', label: 'Comma' },
  { value: '\t', label: 'Tab' },
  { value: ';', label: 'Semicolon' },
  { value: '|', label: 'Pipe' },
  { value: ' ', label: 'Space' },
]

const RULE_OPTIONS: { value: TransformRule['type']; label: string }[] = [
  { value: 'threshold', label: 'Value > threshold' },
  { value: 'range', label: 'Value in range [a, b]' },
  { value: 'median', label: 'Value > column median (auto)' },
  { value: 'column-compare', label: 'Column A > Column B' },
  { value: 'passthrough', label: 'Passthrough (already 0/1)' },
]

const PRESET_ICONS: Record<string, React.ElementType> = {
  mtj: Cpu,
  voltage: Zap,
  binary: Binary,
  continuous: TrendingUp,
  differential: GitCompare,
}

const COLUMN_TYPE_COLORS: Record<ColumnType, string> = {
  numeric: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
  string: 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30',
  binary: 'bg-green-600/20 text-green-400 border-green-600/30',
  mixed: 'bg-orange-600/20 text-orange-400 border-orange-600/30',
  empty: 'bg-zinc-600/20 text-zinc-400 border-zinc-600/30',
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function truncate(s: string, maxLen = 24): string {
  return s.length > maxLen ? s.slice(0, maxLen - 1) + '\u2026' : s
}

function columnName(table: RawTableData, idx: number): string {
  return table.headers?.[idx] ?? `Col ${idx + 1}`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataTransformer({
  rawText,
  fileName,
  fileSize,
  onComplete,
  onCancel,
}: DataTransformerProps) {
  // ----- wizard step -----
  const [step, setStep] = React.useState<1 | 2 | 3>(1)

  // ----- Step 1 state -----
  const [delimiter, setDelimiter] = React.useState('auto')
  const [hasHeader, setHasHeader] = React.useState(true)
  const [commentPrefix, setCommentPrefix] = React.useState('')

  // ----- Step 2 state -----
  const [activePreset, setActivePreset] = React.useState<string | null>(null)
  const [outputMode, setOutputMode] = React.useState<'binary' | 'continuous'>('binary')
  const [selectedColumn, setSelectedColumn] = React.useState(0)
  const [ruleType, setRuleType] = React.useState<TransformRule['type']>('threshold')
  const [threshold, setThreshold] = React.useState('')
  const [rangeMin, setRangeMin] = React.useState('')
  const [rangeMax, setRangeMax] = React.useState('')
  const [compareColumn, setCompareColumn] = React.useState(1)
  const [skipRows, setSkipRows] = React.useState(0)
  const [maxRows, setMaxRows] = React.useState<string>('')
  const [downsample, setDownsample] = React.useState(1)
  const [advancedOpen, setAdvancedOpen] = React.useState(false)

  // ----- Step 3 state -----
  const [transformResult, setTransformResult] = React.useState<ParseResult | null>(null)

  // ----- Parse table -----
  const table = React.useMemo<RawTableData>(() => {
    const opts: { delimiter?: string; hasHeader?: boolean; commentPrefix?: string } = {}
    if (delimiter !== 'auto') opts.delimiter = delimiter
    opts.hasHeader = hasHeader
    if (commentPrefix) opts.commentPrefix = commentPrefix
    return parseRawTable(rawText, opts)
  }, [rawText, delimiter, hasHeader, commentPrefix])

  // Auto-detect initial settings from first parse
  React.useEffect(() => {
    const initial = parseRawTable(rawText)
    setHasHeader(initial.headers !== null)
    if (initial.commentPrefix) setCommentPrefix(initial.commentPrefix)
  }, [rawText])

  // ----- Column stats (memoised) -----
  const colStats = React.useMemo<ColumnStats | null>(() => {
    if (table.rows.length === 0 || table.columnCount === 0) return null
    return computeColumnStats(table.rows, selectedColumn)
  }, [table, selectedColumn])

  // ----- Presets -----
  const presets = React.useMemo<TransformPreset[]>(() => getPresets(), [])

  // ----- Build config -----
  function buildConfig(): TransformConfig {
    const rule: TransformRule = { type: ruleType, sourceColumn: selectedColumn }
    if (ruleType === 'threshold') {
      rule.operator = '>'
      rule.threshold = threshold !== '' ? Number(threshold) : (colStats?.median ?? 0)
    } else if (ruleType === 'range') {
      rule.rangeMin = rangeMin !== '' ? Number(rangeMin) : (colStats?.min ?? 0)
      rule.rangeMax = rangeMax !== '' ? Number(rangeMax) : (colStats?.max ?? 1)
    } else if (ruleType === 'column-compare') {
      rule.compareColumn = compareColumn
    }
    return {
      outputMode,
      rules: [rule],
      selectedColumn,
      skipRows,
      maxRows: maxRows !== '' ? Number(maxRows) : null,
      downsample,
    }
  }

  // ----- Navigate to step 3: run transform -----
  function goToStep3() {
    const config = buildConfig()
    const result = applyTransform(table, config)
    setTransformResult(result)
    setStep(3)
  }

  // ----- Preset click handler -----
  function applyPreset(preset: TransformPreset) {
    setActivePreset(preset.id)
    setOutputMode(preset.outputMode)
    setRuleType(preset.ruleType)
  }

  // ----- Compute output summary for step 3 -----
  const outputValues = React.useMemo<number[]>(() => {
    if (!transformResult) return []
    if (transformResult.kind === 'binary') {
      const { data, bitLength } = transformResult.input
      const bits: number[] = []
      for (let i = 0; i < Math.min(bitLength, 100); i++) {
        bits.push((data[i >>> 3] >>> (7 - (i & 7))) & 1)
      }
      return bits
    } else if (transformResult.kind === 'continuous') {
      return Array.from(transformResult.input.data.slice(0, 100))
    }
    return []
  }, [transformResult])

  const outputStats = React.useMemo(() => {
    if (!transformResult) return null
    if (transformResult.kind === 'binary') {
      const { data, bitLength } = transformResult.input
      let ones = 0
      for (let i = 0; i < bitLength; i++) {
        ones += (data[i >>> 3] >>> (7 - (i & 7))) & 1
      }
      const zeros = bitLength - ones
      return { total: bitLength, ones, zeros, ratio: bitLength > 0 ? ones / bitLength : 0 }
    }
    if (transformResult.kind === 'continuous') {
      const arr = transformResult.input.data
      const n = arr.length
      if (n === 0) return null
      let sum = 0, min = Infinity, max = -Infinity
      for (let i = 0; i < n; i++) {
        sum += arr[i]
        if (arr[i] < min) min = arr[i]
        if (arr[i] > max) max = arr[i]
      }
      const mean = sum / n
      let variance = 0
      for (let i = 0; i < n; i++) variance += (arr[i] - mean) ** 2
      const stdDev = Math.sqrt(variance / n)
      return { count: n, mean, stdDev, min, max }
    }
    return null
  }, [transformResult])

  // ----- Warnings -----
  const warnings = React.useMemo<string[]>(() => {
    const w: string[] = []
    if (!transformResult) return w
    if (transformResult.kind === 'binary') {
      const { data, bitLength } = transformResult.input
      let ones = 0
      for (let i = 0; i < bitLength; i++) {
        ones += (data[i >>> 3] >>> (7 - (i & 7))) & 1
      }
      if (ones === 0 || ones === bitLength) {
        w.push('Warning: All output values are identical. The data may not be suitable for randomness testing.')
      }
    }
    return w
  }, [transformResult])

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="w-full">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Table className="h-5 w-5" />
              Data Transformer
            </CardTitle>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <span
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
                      s === step
                        ? 'bg-primary text-primary-foreground'
                        : s < step
                          ? 'bg-primary/30 text-primary-foreground'
                          : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {s < step ? <Check className="h-3 w-3" /> : s}
                  </span>
                  {s < 3 && <Separator className="w-6" />}
                </React.Fragment>
              ))}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {step === 1 && 'Preview and configure file parsing'}
            {step === 2 && 'Configure column selection and transform rules'}
            {step === 3 && 'Review results and apply'}
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* ============================================================== */}
          {/* STEP 1: FILE PREVIEW                                           */}
          {/* ============================================================== */}
          {step === 1 && (
            <>
              {/* Info bar */}
              <div className="rounded-md bg-muted/50 px-3 py-2 text-sm font-mono">
                {table.rowCount} rows &times; {table.columnCount} columns
                {' | '}Delimiter: {table.delimiter === '\t' ? 'Tab' : table.delimiter === ' ' ? 'Space' : table.delimiter}
                {' | '}Comments: {table.commentPrefix || 'none'}
              </div>

              {/* Controls */}
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="has-header"
                    checked={hasHeader}
                    onCheckedChange={setHasHeader}
                  />
                  <Label htmlFor="has-header" className="text-sm">Has header row</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Delimiter</Label>
                  <Select value={delimiter} onValueChange={setDelimiter}>
                    <SelectTrigger className="w-28 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DELIMITER_OPTIONS.map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Comment prefix</Label>
                  <Input
                    value={commentPrefix}
                    onChange={(e) => setCommentPrefix(e.target.value)}
                    placeholder="#"
                    className="w-16 h-8 font-mono"
                  />
                </div>
              </div>

              {/* Table preview */}
              <div className="rounded-md border overflow-auto max-h-[300px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr className="border-b">
                      {Array.from({ length: table.columnCount }, (_, i) => (
                        <th key={i} className="px-3 py-2 text-left font-medium whitespace-nowrap">
                          <div className="space-y-1">
                            <span>{columnName(table, i)}</span>
                            <div>
                              <Badge
                                variant="outline"
                                className={cn('text-[10px] px-1.5 py-0', COLUMN_TYPE_COLORS[table.columnTypes[i] ?? 'mixed'])}
                              >
                                {table.columnTypes[i] ?? 'mixed'}
                              </Badge>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.slice(0, 20).map((row, ri) => (
                      <tr
                        key={ri}
                        className={cn(
                          'border-b last:border-0',
                          ri % 2 === 1 && 'bg-muted/30',
                        )}
                      >
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-3 py-1.5 font-mono text-xs max-w-[200px] truncate">
                            {truncate(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Navigation */}
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button onClick={() => setStep(2)}>
                  Next <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </>
          )}

          {/* ============================================================== */}
          {/* STEP 2: COLUMN & RULE CONFIGURATION                            */}
          {/* ============================================================== */}
          {step === 2 && (
            <>
              {/* Presets */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Presets</Label>
                <div className="flex flex-wrap gap-2">
                  {presets.map((p) => {
                    const Icon = PRESET_ICONS[p.id] ?? Settings2
                    return (
                      <Card
                        key={p.id}
                        className={cn(
                          'cursor-pointer px-3 py-2 hover:bg-muted/50 transition-colors',
                          activePreset === p.id && 'border-primary ring-1 ring-primary',
                        )}
                        onClick={() => applyPreset(p)}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{p.label}</p>
                            <p className="text-xs text-muted-foreground">{p.description}</p>
                          </div>
                        </div>
                      </Card>
                    )
                  })}
                </div>
              </div>

              <Separator />

              {/* Output mode */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Output Mode</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={cn(
                      'flex-1 rounded-md border px-4 py-2.5 text-sm font-medium transition-colors',
                      outputMode === 'binary'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-muted/50',
                    )}
                    onClick={() => setOutputMode('binary')}
                  >
                    <Binary className="inline mr-2 h-4 w-4" />
                    Binary (0/1)
                  </button>
                  <button
                    type="button"
                    className={cn(
                      'flex-1 rounded-md border px-4 py-2.5 text-sm font-medium transition-colors',
                      outputMode === 'continuous'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-muted/50',
                    )}
                    onClick={() => setOutputMode('continuous')}
                  >
                    <TrendingUp className="inline mr-2 h-4 w-4" />
                    Continuous (float)
                  </button>
                </div>
              </div>

              {/* Source column */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm mb-1 block">Source Column</Label>
                  <Select
                    value={String(selectedColumn)}
                    onValueChange={(v) => setSelectedColumn(Number(v))}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: table.columnCount }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {columnName(table, i)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Rule type (binary only) */}
                {outputMode === 'binary' && (
                  <div>
                    <Label className="text-sm mb-1 block">Rule Type</Label>
                    <Select
                      value={ruleType}
                      onValueChange={(v) => setRuleType(v as TransformRule['type'])}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RULE_OPTIONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Conditional rule parameters */}
              {outputMode === 'binary' && ruleType === 'threshold' && (
                <div>
                  <Label className="text-sm mb-1 block">Threshold</Label>
                  <Input
                    type="number"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    placeholder={colStats ? String(colStats.median) : '0'}
                    className="h-8 w-40 font-mono"
                  />
                </div>
              )}
              {outputMode === 'binary' && ruleType === 'range' && (
                <div className="flex gap-3 items-end">
                  <div>
                    <Label className="text-sm mb-1 block">Min</Label>
                    <Input
                      type="number"
                      value={rangeMin}
                      onChange={(e) => setRangeMin(e.target.value)}
                      placeholder={colStats ? String(colStats.min) : '0'}
                      className="h-8 w-32 font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-sm mb-1 block">Max</Label>
                    <Input
                      type="number"
                      value={rangeMax}
                      onChange={(e) => setRangeMax(e.target.value)}
                      placeholder={colStats ? String(colStats.max) : '1'}
                      className="h-8 w-32 font-mono"
                    />
                  </div>
                </div>
              )}
              {outputMode === 'binary' && ruleType === 'column-compare' && (
                <div>
                  <Label className="text-sm mb-1 block">Compare Column</Label>
                  <Select
                    value={String(compareColumn)}
                    onValueChange={(v) => setCompareColumn(Number(v))}
                  >
                    <SelectTrigger className="h-8 w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: table.columnCount }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {columnName(table, i)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Mini histogram */}
              {colStats && colStats.histogram.length > 0 && (
                <div className="rounded-md border p-3">
                  <Label className="text-xs text-muted-foreground mb-1 block">
                    Distribution: {columnName(table, selectedColumn)}
                  </Label>
                  <ResponsiveContainer width="100%" height={150}>
                    <BarChart
                      data={colStats.histogram.map((h) => ({
                        bin: ((h.binStart + h.binEnd) / 2).toPrecision(4),
                        count: h.count,
                        binStart: h.binStart,
                        binEnd: h.binEnd,
                      }))}
                      margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
                    >
                      <XAxis dataKey="bin" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={40} />
                      <RechartsTooltip
                        contentStyle={{ fontSize: 12, background: 'var(--card)', border: '1px solid var(--border)' }}
                      />
                      <Bar dataKey="count" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                      {outputMode === 'binary' && ruleType === 'threshold' && (
                        <ReferenceLine
                          x={
                            threshold !== ''
                              ? Number(threshold).toPrecision(4)
                              : colStats.median.toPrecision(4)
                          }
                          stroke="hsl(0, 80%, 60%)"
                          strokeDasharray="4 2"
                          label={{ value: 'Threshold', fontSize: 10, fill: 'hsl(0, 80%, 60%)' }}
                        />
                      )}
                      {outputMode === 'binary' && ruleType === 'range' && (
                        <>
                          <ReferenceLine
                            x={(rangeMin !== '' ? Number(rangeMin) : colStats.min).toPrecision(4)}
                            stroke="hsl(0, 80%, 60%)"
                            strokeDasharray="4 2"
                            label={{ value: 'Min', fontSize: 10, fill: 'hsl(0, 80%, 60%)' }}
                          />
                          <ReferenceLine
                            x={(rangeMax !== '' ? Number(rangeMax) : colStats.max).toPrecision(4)}
                            stroke="hsl(0, 80%, 60%)"
                            strokeDasharray="4 2"
                            label={{ value: 'Max', fontSize: 10, fill: 'hsl(0, 80%, 60%)' }}
                          />
                        </>
                      )}
                      {outputMode === 'binary' && ruleType === 'median' && (
                        <ReferenceLine
                          x={colStats.median.toPrecision(4)}
                          stroke="hsl(0, 80%, 60%)"
                          strokeDasharray="4 2"
                          label={{ value: 'Median', fontSize: 10, fill: 'hsl(0, 80%, 60%)' }}
                        />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Advanced options */}
              <div>
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setAdvancedOpen(!advancedOpen)}
                >
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform',
                      advancedOpen && 'rotate-180',
                    )}
                  />
                  <Settings2 className="h-4 w-4" />
                  Advanced Options
                </button>
                {advancedOpen && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-md border p-3">
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <Label className="text-sm">Skip first N rows</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Skip the first N data rows (after any header). Useful for discarding warm-up samples.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Input
                        type="number"
                        min={0}
                        value={skipRows}
                        onChange={(e) => setSkipRows(Math.max(0, Number(e.target.value)))}
                        className="h-8 font-mono"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <Label className="text-sm">Maximum rows</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Limit total output samples. Leave empty to use all available rows.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Input
                        type="number"
                        min={1}
                        value={maxRows}
                        onChange={(e) => setMaxRows(e.target.value)}
                        placeholder="All"
                        className="h-8 font-mono"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <Label className="text-sm">Downsample (every Nth)</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Keep every Nth row to reduce sample size. Value of 1 keeps all rows.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Input
                        type="number"
                        min={1}
                        value={downsample}
                        onChange={(e) => setDownsample(Math.max(1, Number(e.target.value)))}
                        className="h-8 font-mono"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ChevronLeft className="mr-1 h-4 w-4" /> Back
                </Button>
                <Button onClick={goToStep3}>
                  Next <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </>
          )}

          {/* ============================================================== */}
          {/* STEP 3: TRANSFORM PREVIEW & CONFIRM                            */}
          {/* ============================================================== */}
          {step === 3 && transformResult && (
            <>
              {/* Summary */}
              <Card className="bg-muted/30">
                <CardContent className="pt-4 text-sm space-y-1">
                  <p className="font-medium">
                    {outputMode === 'binary'
                      ? `Converted column '${columnName(table, selectedColumn)}' to binary using ${ruleType === 'threshold'
                        ? `threshold > ${threshold || (colStats?.median ?? 0)}`
                        : ruleType === 'range'
                          ? `range [${rangeMin || colStats?.min}, ${rangeMax || colStats?.max}]`
                          : ruleType === 'median'
                            ? 'column median (auto)'
                            : ruleType === 'column-compare'
                              ? `comparison with '${columnName(table, compareColumn)}'`
                              : 'passthrough'
                      }`
                      : `Extracted column '${columnName(table, selectedColumn)}' as continuous values`
                    }
                  </p>
                  <p className="text-muted-foreground">
                    Source: {fileName} ({(fileSize / 1024).toFixed(1)} KB)
                    {' | '}Input rows: {table.rowCount}
                    {' | '}Output samples: {transformResult.input.sampleCount}
                  </p>
                </CardContent>
              </Card>

              {/* Statistics */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  {outputStats && 'ones' in outputStats && (() => {
                    const s = outputStats as { total: number; ones: number; zeros: number; ratio: number }
                    return (
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <p className="text-muted-foreground">Total bits</p>
                          <p className="font-mono font-medium">{s.total.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">1s</p>
                          <p className="font-mono font-medium">{s.ones.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">0s</p>
                          <p className="font-mono font-medium">{s.zeros.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Ratio of 1s</p>
                          <p className="font-mono font-medium">{s.ratio.toFixed(4)}</p>
                        </div>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${s.ratio * 100}%` }}
                        />
                      </div>
                    </div>
                    )
                  })()}
                  {outputStats && 'mean' in outputStats && (() => {
                    const s = outputStats as { count: number; mean: number; stdDev: number; min: number; max: number }
                    return (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Count</p>
                        <p className="font-mono font-medium">{s.count.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Mean</p>
                        <p className="font-mono font-medium">{s.mean.toFixed(6)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Std Dev</p>
                        <p className="font-mono font-medium">{s.stdDev.toFixed(6)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Min</p>
                        <p className="font-mono font-medium">{s.min.toFixed(6)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Max</p>
                        <p className="font-mono font-medium">{s.max.toFixed(6)}</p>
                      </div>
                    </div>
                    )
                  })()}
                </CardContent>
              </Card>

              {/* Warnings */}
              {warnings.length > 0 && (
                <Card className="border-yellow-600/50">
                  <CardContent className="pt-4">
                    {warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-yellow-500">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{w}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Value preview */}
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">
                  Preview (first {Math.min(outputValues.length, 100)} values)
                </Label>
                <div className="rounded-md border bg-muted/30 p-3 max-h-[200px] overflow-auto">
                  {transformResult.kind === 'binary' ? (
                    <p className="font-mono text-xs leading-relaxed break-all">
                      {outputValues.join(' ')}
                    </p>
                  ) : (
                    <pre className="font-mono text-xs leading-relaxed">
                      {outputValues.map((v) => v.toFixed(6)).join('\n')}
                    </pre>
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ChevronLeft className="mr-1 h-4 w-4" /> Back
                </Button>
                <Button onClick={() => onComplete(transformResult)}>
                  <Check className="mr-1 h-4 w-4" /> Apply
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}
